import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BackButton from '../../components/BackButton';
import SettingsButton from '../../components/SettingsButton';
import SocialButton from '../../components/SocialButton';
import HelpButton from '../../components/HelpButton';
import { useTheme } from '../../contexts/ThemeContext';
import { getCharadesDeck } from '../../data/charadesPrompts';
import getButtonStyle from '../../utils/getButtonStyle';
import storageManager from '../../utils/StorageManager';

const CHARADES_GAME_CACHE_KEY = 'charades_game_state';

function CharadesGame() {
    const location = useLocation();
    const navigate = useNavigate();
    const { theme } = useTheme();
    const isMobile = window.innerWidth <= 768;

    const cachedGameState = storageManager.getTemporaryData(CHARADES_GAME_CACHE_KEY) || {};
    const hasRouteState = Array.isArray(location.state?.players) && location.state.players.length > 0;
    const setupState = hasRouteState
        ? location.state
        : (cachedGameState.setup || {});

    const players = setupState.players || [];
    const roundSeconds = setupState.roundSeconds || 60;
    const roundsPerPlayer = setupState.roundsPerPlayer || 2;
    const categories = setupState.categories || ['movies', 'actions', 'animals', 'random'];

    const [turnIndex, setTurnIndex] = useState(hasRouteState ? 0 : (cachedGameState.turnIndex ?? 0));
    const [timeLeft, setTimeLeft] = useState(hasRouteState ? roundSeconds : (cachedGameState.timeLeft ?? roundSeconds));
    const [isRunning, setIsRunning] = useState(hasRouteState ? false : Boolean(cachedGameState.isRunning));
    const [promptCursor, setPromptCursor] = useState(hasRouteState ? 0 : (cachedGameState.promptCursor ?? 0));
    const [revealed, setRevealed] = useState(hasRouteState ? false : Boolean(cachedGameState.revealed));
    const [scores, setScores] = useState(hasRouteState ? {} : (cachedGameState.scores || {}));

    const deck = useMemo(() => getCharadesDeck(categories), [categories]);
    const totalTurns = players.length * roundsPerPlayer;
    const isFinished = players.length === 0 || turnIndex >= totalTurns;
    const currentPlayer = players.length ? players[turnIndex % players.length] : null;
    const currentRound = players.length ? Math.floor(turnIndex / players.length) + 1 : 1;
    const currentPrompt = deck.length ? deck[promptCursor % deck.length] : 'No prompts';

    useEffect(() => {
        if (players.length === 0) {
            storageManager.removeTemporaryData(CHARADES_GAME_CACHE_KEY);
            navigate('/charades-setup');
        }
    }, [players.length, navigate]);

    useEffect(() => {
        if (players.length === 0) return;
        storageManager.saveTemporaryData(CHARADES_GAME_CACHE_KEY, {
            setup: {
                players,
                roundSeconds,
                roundsPerPlayer,
                categories
            },
            turnIndex,
            timeLeft,
            isRunning,
            promptCursor,
            revealed,
            scores
        }, 8);
    }, [
        players,
        roundSeconds,
        roundsPerPlayer,
        categories,
        turnIndex,
        timeLeft,
        isRunning,
        promptCursor,
        revealed,
        scores
    ]);

    useEffect(() => {
        if (!isRunning || isFinished) return undefined;
        if (timeLeft <= 0) {
            setIsRunning(false);
            return undefined;
        }

        const timer = setTimeout(() => {
            setTimeLeft((value) => value - 1);
        }, 1000);
        return () => clearTimeout(timer);
    }, [isRunning, timeLeft, isFinished]);

    useEffect(() => {
        if (timeLeft === 0 && !isFinished) {
            endTurn();
        }
    }, [timeLeft]);

    const startTurn = () => {
        setTimeLeft(roundSeconds);
        setIsRunning(true);
        setRevealed(false);
    };

    const nextPrompt = () => {
        setPromptCursor((value) => value + 1);
        setRevealed(false);
    };

    const awardPoint = () => {
        if (!currentPlayer) return;
        setScores((prev) => ({
            ...prev,
            [currentPlayer]: (prev[currentPlayer] || 0) + 1,
        }));
        nextPrompt();
    };

    const endTurn = () => {
        setIsRunning(false);
        setTurnIndex((value) => value + 1);
        setTimeLeft(roundSeconds);
        nextPrompt();
    };

    const sortedScores = [...players]
        .map((player) => ({ player, score: scores[player] || 0 }))
        .sort((left, right) => right.score - left.score);

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.bgMedium,
            color: theme.textPrimary,
            padding: '20px',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
        }}>
            <BackButton to="/charades-setup" preserveState />
            <HelpButton helpText="Act the prompt without speaking. Tap Correct for each successful guess before time runs out." />
            <SettingsButton />
            <SocialButton />

            <div style={{ marginTop: isMobile ? '45px' : '20px', textAlign: 'center' }}>
                <h1 className="page-title">CHARADES</h1>
            </div>

            {isFinished ? (
                <div style={{
                    maxWidth: '700px',
                    margin: '0 auto',
                    width: '100%',
                    backgroundColor: theme.bgDark,
                    border: `2px solid ${theme.secondary}`,
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'grid',
                    gap: '12px'
                }}>
                    <h2 style={{ margin: 0 }}>Final Scores</h2>
                    {sortedScores.map(({ player, score }) => (
                        <div key={player} style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: theme.bgMedium,
                            borderRadius: '8px',
                            padding: '10px 12px'
                        }}>
                            <span>{player}</span>
                            <strong>{score}</strong>
                        </div>
                    ))}
                    <button
                        onClick={() => {
                            storageManager.removeTemporaryData(CHARADES_GAME_CACHE_KEY);
                            navigate('/charades-setup');
                        }}
                        style={{
                            ...getButtonStyle(theme, { variant: 'accent', padding: '12px 16px' }),
                        }}
                    >
                        Play Again
                    </button>
                </div>
            ) : (
                <div style={{
                    maxWidth: '700px',
                    margin: '0 auto',
                    width: '100%',
                    display: 'grid',
                    gap: '12px'
                }}>
                    <div style={{
                        backgroundColor: theme.bgDark,
                        border: `2px solid ${theme.secondary}`,
                        borderRadius: '12px',
                        padding: '14px',
                        display: 'grid',
                        gap: '6px',
                        textAlign: 'center'
                    }}>
                        <div style={{ color: theme.textSecondary }}>Round {currentRound} of {roundsPerPlayer}</div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>Current Actor: {currentPlayer}</div>
                        <div style={{ fontSize: '1.8rem', fontWeight: 'bold', color: timeLeft <= 10 ? theme.danger : theme.accent }}>
                            {timeLeft}s
                        </div>
                    </div>

                    <div style={{
                        minHeight: '180px',
                        backgroundColor: theme.bgDark,
                        border: `2px solid ${theme.secondary}`,
                        borderRadius: '12px',
                        padding: '16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                        fontSize: isMobile ? '1.5rem' : '2rem',
                        fontWeight: 'bold'
                    }}>
                        {revealed ? currentPrompt : 'Tap Reveal Prompt'}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)', gap: '8px' }}>
                        <button
                            onClick={startTurn}
                            style={{
                                ...getButtonStyle(theme, { variant: 'accent', padding: '12px' }),
                            }}
                        >
                            Start
                        </button>
                        <button
                            onClick={() => setRevealed((value) => !value)}
                            style={{
                                ...getButtonStyle(theme, { variant: 'secondary', padding: '12px' }),
                            }}
                        >
                            {revealed ? 'Hide' : 'Reveal'}
                        </button>
                        <button
                            onClick={awardPoint}
                            style={{
                                ...getButtonStyle(theme, { variant: 'success', padding: '12px' }),
                            }}
                        >
                            Correct
                        </button>
                        <button
                            onClick={endTurn}
                            style={{
                                ...getButtonStyle(theme, { variant: 'danger', padding: '12px' }),
                            }}
                        >
                            End Turn
                        </button>
                    </div>

                    <div style={{
                        backgroundColor: theme.bgDark,
                        border: `2px solid ${theme.secondary}`,
                        borderRadius: '12px',
                        padding: '12px'
                    }}>
                        <div style={{ marginBottom: '8px', color: theme.textSecondary }}>Scoreboard</div>
                        <div style={{ display: 'grid', gap: '6px' }}>
                            {sortedScores.map(({ player, score }) => (
                                <div key={player} style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{player}</span>
                                    <strong>{score}</strong>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default CharadesGame;
